// ⬡B:aba_shared.ccwa_core.hooks.coder_check_in:CODE:banana_pepper_checkin_reader:20260718⬡
// ⬡B:aba_shared.ccwa_core.hooks.coder_check_in:FIX:read_the_real_ccwa_ledger_door_not_findBySource:20260729⬡
// useCoderCheckIn — reads the current temporary-coder check-in through the REAL
// brain door, GET /ccwa/ledger?ham=UID (anew routes/ccwa.checkin.routes.js),
// and normalizes it with the pure coderCheckIn helpers.
//
// WHY NOT client.findBySource: the check-in is not one node at a fixed source
// key. The brain stores many rows under the prefix ccwa.cc.checkin.<coder>.<ts>
// and the only door that reads them and derives the live board is /ccwa/ledger.
// findBySource('ccwa.coder.checkin.current') matched NOTHING in the brain (no
// such source is ever written) and hit the wrong backend (by-source is an
// ababase read; the ledger lives on the A'NU brain), so the badge sat idle
// forever. This hook now calls the real door.
//
// 90/10: all logic lives in the pure normalizer; this hook is only read + poll
// + state plumbing. No founder hardcoded (the ham is a required parameter), no
// strings assembled here beyond the query params the door documents.

import { useEffect, useState, useCallback, useRef } from 'react';
import { resolveAnewApiBase } from '@aba/api-core';
import { normalizeCheckIn, CODER_CHECKIN_LEDGER_PATH } from '../lib/coderCheckIn.js';

const POLL_INTERVAL_MS = 60000;
const DEFAULT_LIMIT = 30;
const BOARD_UNAVAILABLE = 'The activity list is unavailable right now.';

// The board door lives on the A'NEW mind, not the ABABASE adapter used by the
// rest of CCWA. Keep a dedicated override and a verified production fallback.
// VITE_ABA_API_BASE is deliberately not a fallback here because it resolves to
// ABABASE in the live app, where /ccwa/ledger does not exist.
const DEFAULT_BOARD_BASE = resolveAnewApiBase({
  baseUrl: typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env.VITE_CCWA_BOARD_BASE
    : null,
});

export function useCoderCheckIn({
  hamUid,
  apiBase = DEFAULT_BOARD_BASE,
  ccwaKey = null,
  limit = DEFAULT_LIMIT,
  enabled = true,
} = {}) {
  const [checkIn, setCheckIn] = useState(() => normalizeCheckIn(null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hamRef = useRef(hamUid);
  hamRef.current = hamUid;

  const refresh = useCallback(async () => {
    const ham = hamRef.current;
    // Proactive surface, not user-initiated: with no HAM there is nothing to
    // read, so skip silently rather than shout.
    if (!ham) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBase}${CODER_CHECKIN_LEDGER_PATH}?ham=${encodeURIComponent(ham)}&limit=${encodeURIComponent(limit)}`;
      const headers = ccwaKey ? { 'x-ccwa-key': ccwaKey } : undefined;
      const res = await fetch(url, headers ? { headers } : undefined);
      if (!res.ok) {
        // A missing/empty board or an auth gate is not an error to shout about:
        // it just means no coder is live. Only real 5xx set error.
        if (res.status >= 500) setError(BOARD_UNAVAILABLE);
        setCheckIn(normalizeCheckIn(null));
        return;
      }
      const data = await res.json().catch(() => null);
      setCheckIn(normalizeCheckIn(data));
    } catch (e) {
      setError(BOARD_UNAVAILABLE);
      setCheckIn(normalizeCheckIn(null));
    } finally {
      setLoading(false);
    }
  }, [apiBase, ccwaKey, limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [enabled, refresh]);

  return { checkIn, loading, error, refresh };
}
